"use client";

import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/contexts/ToastContext";
import { generateUuid } from "@/lib/uuid";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastPopupProps {
  id?: string; // optional when used standalone
  message: string;
  type: ToastType;
  duration?: number;
  onClose?: () => void;
}

const toastIcons = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const toastStyles = {
  success: "bg-green-50 border-green-200 text-green-800",
  error: "bg-red-50 border-red-200 text-red-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
};

const iconStyles = {
  success: "text-green-600",
  error: "text-red-600",
  info: "text-blue-600",
  warning: "text-yellow-600",
};

export function ToastPopup({
  message,
  type,
  duration = 5000,
  onClose,
}: ToastPopupProps) {
  const [isVisible, setIsVisible] = useState(true);
  const Icon = toastIcons[type];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        // container for each toast (controls width + animation)
        "pointer-events-auto w-full sm:w-[380px] max-w-[92vw]",
        "transition-all duration-300 ease-out",
        "animate-in fade-in slide-in-from-top-2"
      )}
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          "flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg",
          "break-words",
          toastStyles[type]
        )}
      >
        <Icon className={cn("w-5 h-5 mt-0.5 flex-shrink-0", iconStyles[type])} />
        <span className="text-sm font-medium leading-5 flex-1">
          {message}
        </span>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Local hook version if you’re not using the ToastContext.
 * (Leaves auto-removal to the ToastPopup via onClose to avoid double timers.)
 */
export function useToastPopup() {
  const [toasts, setToasts] = useState<
    Array<ToastPopupProps & { id: string }>
  >([]);

  const makeId = () => generateUuid();

  const showToast = (
    message: string,
    type: ToastType = "info",
    duration: number = 5000
  ) => {
    const id = makeId();
    const newToast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showSuccess = (message: string, duration?: number) =>
    showToast(message, "success", duration ?? 5000);
  const showError = (message: string, duration?: number) =>
    showToast(message, "error", duration ?? 5000);
  const showInfo = (message: string, duration?: number) =>
    showToast(message, "info", duration ?? 5000);
  const showWarning = (message: string, duration?: number) =>
    showToast(message, "warning", duration ?? 5000);

  return {
    toasts,
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    removeToast,
  };
}

/**
 * Toast container that reads from your ToastContext.
 * Place once near the root of your app (e.g., in Layout).
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div
      className={cn(
        // top-right stack
        "fixed top-4 right-4 z-50",
        // stack spacing & click-through except on toasts themselves
        "flex flex-col gap-2 pointer-events-none"
      )}
    >
      {toasts.map((toast) => (
        <ToastPopup
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration ?? 5000}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}
